import { memo } from 'react'
import { AppBar, SxProps } from '@mui/material'
import clsx from 'clsx'

import { Link } from '@/components/Navigation'
import { Box, Container } from '@/components/Layout'
import {
  FanaticsLogo,
  FanaticsIcon,
  BookSeatsLogo,
  BookSeatsStackedIcon,
  BookSeatsIcon,
} from '@/assets'
import { ROUTES, useDomainInfo } from '@/utils'
import { withErrorBoundary } from '@/components/Utils'

import HeaderActions from './HeaderActions'
import HeaderEventInfo from './HeaderEventInfo'
import { headerIds, headerClasses } from './Header.data'
import { HeaderProps } from './Header.types'
import { NavigationMenu } from './NavigationMenu'
import MobileNavigationMenu from './NavigationMenuMobile'
import { headerStyles as styles } from './Header.style'

function Header({
  packageInfo,
  theme,
  transparent = false,
  hasBanner = false,
  hidePackageInfo,
  pageConfig,
}: HeaderProps) {
  const { isFanaticsDomain } = useDomainInfo()
  const isPackageWorkflow = Boolean(packageInfo)

  const desktopConfigLogo = pageConfig?.headerLogo.desktop
  const mobileConfigLogo = pageConfig?.headerLogo.mobile

  // Both logos must be present to use partner branding — partial config is ignored
  const hasConfigLogo = !!(desktopConfigLogo && mobileConfigLogo)

  return (
    <AppBar
      id={headerIds.root}
      sx={styles.header}
      className={clsx({
        [headerClasses.package]: isPackageWorkflow,
        [headerClasses.transparent]: transparent,
      })}
      position="static">
      <Container className="h-100" sx={styles.containerWrapper}>
        <Container className={`${headerClasses.container} h-100`} sx={styles.container} fixed>
          <Box sx={styles.leftContent}>
            <Link className={`${headerClasses.logoWrapper} flex`} to={ROUTES.HOME}>
              {/* Default BookSeats branding — shown when no partner config logos are provided.
                  CSS classes control which variant is visible per breakpoint/workflow:
                  - BookSeatsLogo        → desktop (lg+)
                  - BookSeatsStackedIcon → mobile, non-package workflow
                  - BookSeatsIcon        → mobile, package workflow only */}
              {!hasConfigLogo && (
                <>
                  {isFanaticsDomain ? (
                    <>
                      <FanaticsLogo
                        className={`${headerClasses.logo} ${headerClasses.logoFanatics}`}
                      />
                      <FanaticsIcon
                        className={`${headerClasses.logo} ${headerClasses.logoFanaticsIcon}`}
                      />
                    </>
                  ) : (
                    <>
                      <BookSeatsLogo className={headerClasses.logo} />
                      <BookSeatsStackedIcon
                        className={`${headerClasses.logo} ${headerClasses.logoStackedIcon}`}
                      />
                      <BookSeatsIcon
                        className={`${headerClasses.logo} ${headerClasses.logoIcon}`}
                      />
                    </>
                  )}
                </>
              )}

              {/* Partner/config branding — replaces BookSeats logos when both desktop and
                  mobile config logos are set via pageConfig.headerLogo.
                  - Desktop (lg+)        → always shows configLogoDesktop
                  - Mobile, non-package  → shows configLogoMobile
                  - Mobile, package      → falls back to BookSeatsIcon (partner logo hidden) */}
              {hasConfigLogo && (
                <>
                  <Box sx={[styles.configLogo.root, styles.configLogo.desktop] as SxProps}>
                    <picture>
                      <img
                        src={desktopConfigLogo}
                        alt=""
                        loading="lazy"
                        className={headerClasses.configLogoImage}
                      />
                    </picture>
                  </Box>

                  {!isPackageWorkflow && (
                    <Box sx={[styles.configLogo.root, styles.configLogo.mobile] as SxProps}>
                      <picture>
                        <img
                          src={mobileConfigLogo}
                          alt=""
                          loading="lazy"
                          className={headerClasses.configLogoImage}
                        />
                      </picture>
                    </Box>
                  )}
                  {isPackageWorkflow && isFanaticsDomain && (
                    <FanaticsLogo
                      className={`${headerClasses.logo} ${headerClasses.logoIcon} ${headerClasses.logoFanatics}`}
                    />
                  )}
                  {isPackageWorkflow && !isFanaticsDomain && (
                    <BookSeatsIcon className={`${headerClasses.logo} ${headerClasses.logoIcon}`} />
                  )}
                </>
              )}
            </Link>

            <NavigationMenu />
          </Box>

          <MobileNavigationMenu />
          <HeaderEventInfo packageInfo={packageInfo} hidePackageInfo={hidePackageInfo} />
          <HeaderActions packageInfo={packageInfo} hidePackageInfo={hidePackageInfo} />
        </Container>
      </Container>
      {theme && (
        <Box
          sx={
            [
              styles.headerColorOverlay,
              { bgcolor: theme.primaryColor },
              !hasBanner && styles.headerGradient,
            ] as SxProps
          }
        />
      )}
    </AppBar>
  )
}

export default memo(withErrorBoundary(Header))
